import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';

import { AjsResponse, wrapAjs } from '../ajs-response';
import { buildParams, PAGINATION_FIELDS } from '../http-params.util';

/**
 * UserProxyApiService (Angular 18) — tenant user management for B2B portal.
 *
 * Replaces apps/b2b-ui/common/services/api/proxyUserApi/proxyUserApi.service.js.
 *
 * Registered as `apiService.userProxy.*` (the legacy facade property name).
 * Three sub-namespaces (permissions, titles, departments).
 *
 * Legacy quirks preserved 1:1:
 *   - `search()` always appends an empty `search` parameter, even when the
 *     caller did not supply one (the backend treats `search=` as "match all").
 *   - `invite(emails)` POSTs an empty body but sets `x-acme-emails-invite` to
 *     the comma-separated email list as the actual payload.
 */
const CUSTOMER_USERS_BASE_V1 = '/v1/api/customer/users';
const CUSTOMER_USERS_BASE_V2 = '/v2/api/customer/users';

const SEARCH_FIELDS = ['search', 'page', 'pageSize', 'sortDirection', 'sortField', 'totalItems'] as const;

const LIST_FIELDS = [
  ...PAGINATION_FIELDS,
  'departmentId',
  'search',
  'role',
  'jobTitle',
  'activeOnly',
  'passwordStatus',
] as const;

const PERMISSIONS_LIST_FIELDS = [...PAGINATION_FIELDS, 'departmentId', 'role', 'jobTitle', 'activeOnly'] as const;

interface UserCreateProps {
  userName: string;
  password?: string;
  firstName: string;
  lastName: string;
  email: string;
  title?: string;
  phoneNumber?: string;
  tenantId: number | string;
}

@Injectable({ providedIn: 'root' })
export class UserProxyApiService {
  private readonly http = inject(HttpClient);

  search(props?: Record<string, unknown>): Promise<AjsResponse<unknown>> {
    let params = buildParams(props, SEARCH_FIELDS);
    if (!params.has('search')) {
      params = params.set('search', '');
    }
    return wrapAjs(this.http.get<unknown>(`${CUSTOMER_USERS_BASE_V2}/names`, { params }));
  }

  invite(emails: string): Promise<AjsResponse<unknown>> {
    const headers = new HttpHeaders({ 'x-acme-emails-invite': emails });
    return wrapAjs(
      this.http.post<unknown>(`${CUSTOMER_USERS_BASE_V1}/invite`, null, {
        headers,
      }),
    );
  }

  resendInvite(id: number | string): Promise<AjsResponse<unknown>> {
    return wrapAjs(this.http.post<unknown>(`${CUSTOMER_USERS_BASE_V1}/${id}/resendConfirmation`, null));
  }

  view(userId: number | string): Promise<AjsResponse<unknown>> {
    return wrapAjs(this.http.get<unknown>(`${CUSTOMER_USERS_BASE_V1}/${userId}`));
  }

  list(props?: Record<string, unknown>): Promise<AjsResponse<unknown>> {
    return wrapAjs(
      this.http.get<unknown>(CUSTOMER_USERS_BASE_V2, {
        params: buildParams(props, LIST_FIELDS),
      }),
    );
  }

  create(props: UserCreateProps): Promise<AjsResponse<unknown>> {
    return wrapAjs(
      this.http.post<unknown>(CUSTOMER_USERS_BASE_V1, {
        userName: props.userName,
        password: props.password,
        firstName: props.firstName,
        lastName: props.lastName,
        email: props.email,
        title: props.title,
        phoneNumber: props.phoneNumber,
        tenantId: props.tenantId,
      }),
    );
  }

  readonly permissions = {
    list: (props?: Record<string, unknown>): Promise<AjsResponse<unknown>> =>
      wrapAjs(
        this.http.get<unknown>(`${CUSTOMER_USERS_BASE_V2}/permissions`, {
          params: buildParams(props, PERMISSIONS_LIST_FIELDS),
        }),
      ),

    update: (props: { id: number | string; users: unknown }): Promise<AjsResponse<unknown>> =>
      wrapAjs(this.http.put<unknown>(`${CUSTOMER_USERS_BASE_V1}/permissions/${props.id}`, props.users)),
  };

  readonly titles = {
    list: (props?: Record<string, unknown>): Promise<AjsResponse<unknown>> =>
      wrapAjs(
        this.http.get<unknown>(`${CUSTOMER_USERS_BASE_V2}/titles`, {
          params: buildParams(props, PERMISSIONS_LIST_FIELDS),
        }),
      ),
  };

  readonly departments = {
    update: (props: { id: number | string; users: unknown }): Promise<AjsResponse<unknown>> =>
      wrapAjs(this.http.put<unknown>(`${CUSTOMER_USERS_BASE_V1}/departments/${props.id}`, props.users)),
  };
}
